import { inject, Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import {
  Observable,
  catchError,
  switchMap,
  throwError,
  BehaviorSubject,
  filter,
  take,
  interval,
  map,
  throwIfEmpty,
} from 'rxjs';
import { AuthService } from '../../features/auth/application/service/auth.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private authService = inject(AuthService);

  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<boolean>(false);
  private readonly refreshLockKey = 'ui_auth_refresh_lock';
  private readonly refreshLockTtlMs = 12000;
  private readonly refreshLockPollMs = 200;
  private readonly refreshLockMaxWaitMs = 14000;
  private readonly refreshLockOwner = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (req.url.includes('/auth/refresh')) {
      return next.handle(req).pipe(
        catchError((error) => {
          if (error instanceof HttpErrorResponse && error.status === 401) {
            this.forceSignOutAndRedirect();
          }
          return throwError(() => error);
        }),
      );
    }

    if (req.url.includes('/auth/')) {
      return next.handle(req);
    }

    return next.handle(req).pipe(
      catchError((error) => {
        if (
          error instanceof HttpErrorResponse &&
          error.status === 401 &&
          !req.url.includes('/auth/refresh')
        ) {
          return this.handle401Error(req, next);
        }
        return throwError(() => error);
      }),
    );
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(false);

      if (!this.tryAcquireGlobalRefreshLock()) {
        return this.waitForGlobalRefreshUnlock().pipe(
          switchMap(() => {
            this.isRefreshing = false;
            this.refreshTokenSubject.next(true);
            return next.handle(request);
          }),
          catchError((error) => {
            this.isRefreshing = false;
            this.refreshTokenSubject.next(false);
            return throwError(() => error);
          }),
        );
      }

      return this.authService.refreshToken().pipe(
        catchError((error) => {
          this.releaseGlobalRefreshLock();
          this.isRefreshing = false;
          this.refreshTokenSubject.next(false);
          this.forceSignOutAndRedirect();

          return throwError(() => error);
        }),
        switchMap(() => {
          this.releaseGlobalRefreshLock();
          this.isRefreshing = false;
          this.refreshTokenSubject.next(true);
          return next.handle(request);
        }),
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter((ready) => ready),
        take(1),
        switchMap(() => next.handle(request)),
      );
    }
  }

  private tryAcquireGlobalRefreshLock(): boolean {
    if (typeof localStorage === 'undefined') {
      return true;
    }

    const now = Date.now();
    const raw = localStorage.getItem(this.refreshLockKey);
    const current = raw ? this.safeParseLock(raw) : null;

    if (current && current.expiresAt > now && current.owner !== this.refreshLockOwner) {
      return false;
    }

    const nextLock = {
      owner: this.refreshLockOwner,
      expiresAt: now + this.refreshLockTtlMs,
    };
    localStorage.setItem(this.refreshLockKey, JSON.stringify(nextLock));

    const saved = this.safeParseLock(localStorage.getItem(this.refreshLockKey) ?? '');
    return !!saved && saved.owner === this.refreshLockOwner;
  }

  private releaseGlobalRefreshLock(): void {
    if (typeof localStorage === 'undefined') {
      return;
    }

    const current = this.safeParseLock(localStorage.getItem(this.refreshLockKey) ?? '');
    if (!current || current.owner !== this.refreshLockOwner) {
      return;
    }

    localStorage.removeItem(this.refreshLockKey);
  }

  private waitForGlobalRefreshUnlock(): Observable<void> {
    if (typeof localStorage === 'undefined') {
      return throwError(() => new Error('localStorage unavailable'));
    }

    const checks = Math.ceil(this.refreshLockMaxWaitMs / this.refreshLockPollMs);
    return interval(this.refreshLockPollMs).pipe(
      take(checks),
      map(() => this.isGlobalRefreshLockReleased()),
      filter((released) => released),
      take(1),
      throwIfEmpty(() => new Error('Timed out waiting for refresh lock release')),
      map(() => void 0),
    );
  }

  private isGlobalRefreshLockReleased(): boolean {
    if (typeof localStorage === 'undefined') {
      return true;
    }

    const now = Date.now();
    const current = this.safeParseLock(localStorage.getItem(this.refreshLockKey) ?? '');
    if (!current) {
      return true;
    }

    return current.expiresAt <= now;
  }

  private safeParseLock(raw: string): { owner: string; expiresAt: number } | null {
    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw);
      if (
        typeof parsed?.owner === 'string' &&
        typeof parsed?.expiresAt === 'number' &&
        Number.isFinite(parsed.expiresAt)
      ) {
        return parsed;
      }
    } catch {
      return null;
    }

    return null;
  }

  private redirectToLogin(): void {
    if (typeof window === 'undefined') {
      return;
    }

    if (!window.location.pathname.includes('/auth/sign-in')) {
      window.location.assign('/auth/sign-in');
    }
  }

  private forceSignOutAndRedirect(): void {
    const clearedCurrentSession = this.authService.clearUserOnlyIfCurrentSession();

    if (!clearedCurrentSession) {
      // Another tab already established a newer session; avoid deleting shared auth state.
      this.authService.refreshUser();
      return;
    }

    this.redirectToLogin();
  }
}


